/**
 * Delivery Preferences Card Component
 * Email toggle and frequency selection
 */

import { Send, Loader2 } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { FREQUENCY_OPTIONS } from '../utils/constants';
import type { EmailFrequency } from '../types';

interface DeliveryPreferencesCardProps {
  emailEnabled: boolean;
  emailFrequency: EmailFrequency;
  isLoading: boolean;
  onToggleEmail: (enabled: boolean) => void;
  onChangeFrequency: (frequency: EmailFrequency) => void;
}

export function DeliveryPreferencesCard({
  emailEnabled,
  emailFrequency,
  isLoading,
  onToggleEmail,
  onChangeFrequency,
}: DeliveryPreferencesCardProps) {
  const { t } = useLanguage();

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-primary-200 transition-all duration-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <Send className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">
              {t('notifications.delivery.title', 'Delivery Preferences')}
            </h2>
            <p className="text-sm text-gray-500">
              {t('notifications.delivery.subtitle', 'How you receive watchlist alerts')}
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Email toggle */}
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium text-gray-900">
              {t('notifications.delivery.emailToggle', 'Email Notifications')}
            </div>
            <div className="text-sm text-gray-500">
              {t('notifications.delivery.emailDescription', 'Receive alerts via email')}
            </div>
          </div>
          <button
            onClick={() => onToggleEmail(!emailEnabled)}
            disabled={isLoading}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
              emailEnabled ? 'bg-primary-600' : 'bg-gray-200'
            }`}
          >
            {isLoading ? (
              <span className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="w-4 h-4 animate-spin text-white" />
              </span>
            ) : (
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  emailEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            )}
          </button>
        </div>

        {/* Frequency selection (shown when email enabled) */}
        {emailEnabled && (
          <div className="space-y-3 pt-2 border-t border-gray-100">
            <label className="block text-sm font-medium text-gray-700">
              {t('notifications.delivery.frequencyLabel', 'How often?')}
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {FREQUENCY_OPTIONS.map(option => (
                <button
                  key={option.value}
                  onClick={() => onChangeFrequency(option.value)}
                  disabled={isLoading}
                  className={`p-3 rounded-xl border-2 text-left transition-all disabled:opacity-50 ${
                    emailFrequency === option.value
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div
                    className={`font-medium text-sm ${
                      emailFrequency === option.value ? 'text-primary-900' : 'text-gray-900'
                    }`}
                  >
                    {t(option.labelKey, option.label)}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {t(option.descKey, option.description)}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
